import ApiProvider from "@/api/baseApi/api-provider";
import RequestConfig from "@/api/baseApi/request-config";
import {
  deleteTokens,
  getAccessToken,
  getRefreshToken,
  storeAuthTokens,
  getLangCookie,
} from "@/utils/functions/cookieOperations";
import { i18n } from "../../../i18n-config";

/**
 * One action per page lifetime. A lapsed tenant fails EVERY in-flight request, and we want
 * a single navigation rather than one per rejected promise. Module scope, so it is shared
 * across every service instance built on this class.
 */
let outOfSubscriptionHandled = false;

export default class BaseClass extends ApiProvider {
  constructor(config: RequestConfig) {
    super({ ...config });
    this.instance.interceptors.request.use(
      (config) => {
        const token = getAccessToken();
        if (token) {
          config.headers["Authorization"] = "Bearer " + token;
        }

        // Ensure Accept-Language is set dynamically on every request.
        // This prevents backend language fallback for authenticated users during
        // client-side refetches or when closing over stale apiConfig instances.
        // Priority: URL path > cookie > existing header
        if (typeof window !== "undefined") {
          const langFromPath = getLangFromPath(window.location.pathname);
          const langFromCookie = getLangCookie();
          
          // Only override if there's a mismatch between path and cookie
          // This indicates the cookie hasn't been updated yet by middleware
          if (langFromPath && langFromPath !== langFromCookie) {
            config.headers["Accept-Language"] = langFromPath;
          }
        }

        return config;
      },
      (error) => Promise.reject(error),
    );
    this.instance.interceptors.response.use(
      (res) => res,
      async (err) => {
        const originalConfig = err.config;
        if (err.response) {
          // Suspended (430) / deactivated (431) tenant, detected CLIENT-side.
          //
          // Server-side this is already handled: the proxy short-circuits a lapsed tenant
          // to /company-error before anything renders. This branch exists only for a lapse
          // that happens MID-SESSION, after hydration — where every queued query (cart,
          // checkout, profile…) would otherwise fail one by one with no explanation.
          //
          // Placed BEFORE the 404/431 trim below on purpose: that branch returns early, so
          // a 431 handled after it would never reach here. We only dispatch and fall
          // through, so the trim still applies to 431 exactly as before.
          //
          // No cookie is written. A persisted client verdict could outlive the lapse and
          // keep a RECOVERED store blocked — the failure mode this whole story removes.
          // The in-memory flag lasts the page lifetime, and the navigation below ends it.
          if (
            typeof window !== "undefined" &&
            (err.response.status === 430 || err.response.status === 431) &&
            !outOfSubscriptionHandled
          ) {
            outOfSubscriptionHandled = true;
            window.dispatchEvent(new CustomEvent("company:out-of-subscription"));
          }

          // Reduce log size for 404 and 431 errors to prevent server storage bloat
          if (err.response?.status === 404 || err.response?.status === 431) {
            err.message = `${err.response.status} Error - ${err.config?.url || "unknown"}`;
            err.config = {
              url: err.config?.url,
              method: err.config?.method,
            } as any;
            err.request = undefined;
            if (err.response) {
              err.response.data = undefined;
              err.response.config = undefined;
              err.response.request = undefined;
            }
            return Promise.reject(err);
          }

          if (err.response?.status === 401 && !originalConfig._retry) {
            const refreshToken = getRefreshToken();
            if (!refreshToken) return Promise.reject(err);
            originalConfig._retry = true;
            try {
              const rs = await this.instance.post("/users/refresh-token", {
                refresh_token: refreshToken,
              });
              const { access_token, refresh_token } = rs.data.result;
              storeAuthTokens(access_token, refresh_token);
              return this.instance(originalConfig);
            } catch (_error: any) {
              if (
                _error.response?.status === 422 ||
                _error.response?.status === 405
              ) {
                deleteTokens();
                // Dispatch event so React components can sync auth state
                if (typeof window !== "undefined") {
                  window.dispatchEvent(new CustomEvent("auth:tokens-deleted"));
                }
              }
              return Promise.reject(_error);
            }
          }
        }
        return Promise.reject(err);
      },
    );
  }
}

/**
 * Extracts language from URL pathname.
 * Returns undefined if no valid language found in path.
 * Safe for both client and server (returns undefined on server).
 */
const getLangFromPath = (pathname: string): "ar" | "en" | undefined => {
  if (!pathname) return undefined;
  
  const segments = pathname.split("/");
  // segments[0] is empty string before first /
  // segments[1] is the potential language code
  const potentialLang = segments[1];
  
  // Check if it's a valid locale from i18n config
  return i18n.locales.includes(potentialLang as any) 
    ? (potentialLang as "ar" | "en") 
    : undefined;
};
